import { AccountViewer } from "components/form/graphql/types";
import { Action } from "types/generated";

export type AccessResult = {
  result: boolean;
  message?: string;
};

export const NO_ACCESS_MESSAGE = "You don't have permission to perform this action.";

export const hasAtLeastOneRequiredAction = (
  requiredActions: Action[], // one of these actions is required
  viewer: AccountViewer,
  spaceId?: string
) => {
  if (viewer.admin) {
    return true;
  }

  if (!spaceId) {
    return false;
  }

  return !!viewer.actions?.find(
    (item) =>
      item.spaceID === spaceId && requiredActions.find((action) => item.actions.includes(action)),
    [] as Action[]
  );
};

/**
 * Checks if a user has all of the required actions in at least one space
 * @param requiredActions Array of actions to check
 * @param viewer AccountViewer object with user details
 * @returns true if user has all of the required actions in at least one space
 */
export const hasAllRequiredActionsInAnySpace = (
  requiredActions: Action[],
  viewer: AccountViewer
): boolean => {
  if (viewer.admin) {
    return true;
  }

  if (!viewer.actions?.length) {
    return false;
  }

  return viewer.actions.some((item) =>
    requiredActions.every((action) => item.actions.includes(action))
  );
};

/**
 * Checks if a user has at least one of the required actions in at least one space
 * @param requiredActions Array of actions to check
 * @param viewer AccountViewer object with user details
 * @returns true if user has all of the required actions in at least one space
 */
export const hasAnyRequiredActionsInAnySpace = (
  requiredActions: Action[],
  viewer: AccountViewer
): boolean => {
  if (viewer.admin) {
    return true;
  }

  if (!viewer.actions?.length) {
    return false;
  }

  return viewer.actions.some((item) =>
    requiredActions.some((action) => item.actions.includes(action))
  );
};

// TODO: [AAC] improve naming to include "space" in the name of these functions
const MANAGE_ACCESS_TYPES = [Action.SpaceAdmin];

// TODO: Make required spaceId when we improve gql query types
export const hasManageAccess = (viewer: AccountViewer, spaceId?: string) =>
  hasAtLeastOneRequiredAction(MANAGE_ACCESS_TYPES, viewer, spaceId);

const WRITE_ACCESS_TYPES = [Action.SpaceWrite];

// TODO: Make required spaceId when we improve gql query types
export const hasWriteAccess = (viewer: AccountViewer, spaceId?: string) =>
  hasAtLeastOneRequiredAction(WRITE_ACCESS_TYPES, viewer, spaceId);

// TODO: Make required spaceId when we improve gql query types
export const hasAtLeastWriteAccess = (viewer: AccountViewer, spaceId?: string) =>
  hasWriteAccess(viewer, spaceId) || hasManageAccess(viewer, spaceId);

const READ_ACCESS_TYPES = [Action.SpaceRead];

// TODO: Make required spaceId when we improve gql query types
const hasReadAccess = (viewer: AccountViewer, spaceId?: string) =>
  hasAtLeastOneRequiredAction(READ_ACCESS_TYPES, viewer, spaceId);

export const hasAtLeastReadAccess = (viewer: AccountViewer, spaceId?: string) =>
  hasReadAccess(viewer, spaceId) ||
  hasWriteAccess(viewer, spaceId) ||
  hasManageAccess(viewer, spaceId);
